import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";

const ACTIONS = ["All", "created", "updated", "deleted", "assigned", "removed", "granted", "enabled", "disabled"];

export default function ActivityLogs() {
  const [entityTypes, setEntityTypes] = useState(["All"]);
  const [entityType, setEntityType] = useState("All");
  const [action, setAction] = useState("All");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const limit = 50;

  useEffect(() => {
    api.get("/activity-logs/entity-types").then((r) => setEntityTypes(["All", ...r.data])).catch(() => {});
  }, []);

  const load = async (nextSkip = 0) => {
    const params = { skip: nextSkip, limit };
    if (entityType !== "All") params.entity_type = entityType;
    if (action !== "All") params.action = action;
    const r = await api.get("/activity-logs", { params });
    setItems((prev) => (nextSkip === 0 ? r.data.items : [...prev, ...r.data.items]));
    setTotal(r.data.total);
  };

  useEffect(() => {
    load(0);
  }, [entityType, action]);

  return (
    <DashboardLayout title="Activity Logs">
      <div className="flex items-center gap-3 mb-5">
        <Select value={entityType} onValueChange={setEntityType}>
          <SelectTrigger data-testid="activity-entity-filter" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            {entityTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={action} onValueChange={setAction}>
          <SelectTrigger data-testid="activity-action-filter" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ACTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-[#4B5563] ml-auto">{total} total events</span>
      </div>

      <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="activity-logs-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-[#4B5563] border-b border-[#E2E8F0] bg-[#F8F9FA]">
              <th className="px-5 py-3 font-medium">When</th>
              <th className="px-5 py-3 font-medium">Actor</th>
              <th className="px-5 py-3 font-medium">Action</th>
              <th className="px-5 py-3 font-medium">Entity</th>
              <th className="px-5 py-3 font-medium">Details</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-[#4B5563]">No activity recorded yet.</td></tr>}
            {items.map((log) => (
              <tr key={log.id} data-testid={`activity-row-${log.id}`} className="border-b border-[#E2E8F0] last:border-b-0">
                <td className="px-5 py-3 text-[#4B5563] whitespace-nowrap">{new Date(log.created_at).toLocaleString()}</td>
                <td className="px-5 py-3 text-[#1C1C1E] font-medium">{log.actor_name} <span className="text-xs text-[#4B5563]">({log.actor_role})</span></td>
                <td className="px-5 py-3 capitalize text-[#00823B]">{log.action}</td>
                <td className="px-5 py-3 text-[#4B5563]">{log.entity_type.replace(/_/g, " ")}: {log.entity_label}</td>
                <td className="px-5 py-3 text-[#4B5563]">{log.details || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {items.length < total && (
        <div className="flex justify-center mt-4">
          <Button variant="outline" data-testid="activity-load-more-button" onClick={() => load(items.length)}>Load More</Button>
        </div>
      )}
    </DashboardLayout>
  );
}
